import React, { useEffect, useRef, useState, useContext } from "react"

import { PlateView, ViewBounds } from "../../../../plate_viewer"
import type { IdeaColourMode } from "../../../../plate_viewer/idea_colour"
import ContextMenu, { ContextMenuState } from "./show_plate_view/context_menu"
import TitleHover, { TitleHoverState } from "./show_plate_view/title_hover"
import FleetingIdeaPanel from "./show_plate_view/fleeting_idea_panel"
import { FleetingIdea, ID, Location } from "../../../../model/types"
import { useNana } from "../../../hooks/useNana"
import { DeviceContext } from "../../../providers/device_context"
import TutorialComponent from "./tutorial_component"
import { MosaicPageState } from "../mosaic"

const ShowPlateView = ({
  plateViewDraggingHappening,
  setPlateViewDraggingHappening,
  openLocation,
  initialViewBounds,
  locked,
  setLocked,
  colourMode,
  setColourMode,
}: {
  plateViewDraggingHappening: boolean
  setPlateViewDraggingHappening: (b: boolean) => void
  openLocation: Location | null
  initialViewBounds: ViewBounds | null
  locked: boolean
  setLocked: (b: boolean) => void
  colourMode: IdeaColourMode
  setColourMode: (mode: IdeaColourMode) => void
}) => {
  const ref = useRef(null)
  const { model, commander, hub, plateView, setPlateView, local } = useNana()
  const [contextMenuState, setContextMenuState] = useState<ContextMenuState | null>(null)
  const [titleOpenAt, setTitleHoverAt] = useState<TitleHoverState | null>(null)
  const [draggingFleetingIdea, setDraggingFleetingIdea] = useState<FleetingIdea | null>(null)
  const pendingChangedViewBoundsRef = useRef<ViewBounds | null>(null)
  const deviceContext = useContext(DeviceContext)
  const clickHereFirstRef = useRef<HTMLDivElement | null>(null)

  if (local == null) throw "Local should not be null in ShowPlateView"

  useEffect(() => {
    local?.getLockState().then((lockState: boolean | null) => {
      if (lockState != null) setLocked(lockState)
    })
  }, [local])

  useEffect(() => {
    const interval = setInterval(() => {
      if (pendingChangedViewBoundsRef.current)
        local.saveViewBounds(pendingChangedViewBoundsRef.current)
      pendingChangedViewBoundsRef.current = null
    }, 1000)

    return () => {
      clearInterval(interval)
      if (pendingChangedViewBoundsRef.current)
        local.saveViewBounds(pendingChangedViewBoundsRef.current)
      pendingChangedViewBoundsRef.current = null
    }
  }, [local, pendingChangedViewBoundsRef])

  const lockedRef = useRef(false)
  useEffect(() => {
    lockedRef.current = locked
    local.saveLockState(locked)
    MosaicPageState.locked = locked
  }, [locked])

  useEffect(() => {
    if (ref.current) {
      if (plateView == null) {
        const plateView = new PlateView(
          setContextMenuState,
          setTitleHoverAt,
          setPlateViewDraggingHappening,
          openLocation,
          (vb: ViewBounds) => (pendingChangedViewBoundsRef.current = vb),
          initialViewBounds,
          deviceContext,
          lockedRef,
          clickHereFirstRef,
        )
        plateView.setDomElement(ref.current as HTMLElement)
        setPlateView(plateView)
        plateView.setup(model, commander, hub)
        plateView.draggingFleetingIdea = draggingFleetingIdea
        return () => {
          plateView.clearDomElement()
        }
      } else {
        plateView.setDomElement(ref.current as HTMLElement)
        plateView.setContextMenuState = setContextMenuState
        plateView.setTitleHoverAt = setTitleHoverAt
        plateView.setPlateViewDraggingHappening = setPlateViewDraggingHappening
        plateView.lockedRef = lockedRef
      }
    }
  }, [lockedRef, ref.current, setContextMenuState, setTitleHoverAt, setPlateViewDraggingHappening])

  useEffect(() => {
    if (plateView) plateView.draggingFleetingIdea = draggingFleetingIdea
  }, [plateView, draggingFleetingIdea])

  useEffect(() => {
    if (plateView) plateView.setColourMode(colourMode)
  }, [plateView, colourMode])

  return (
    <>
      <TutorialComponent clickHereFirstRef={clickHereFirstRef} />
      <ContextMenu state={contextMenuState} />
      <TitleHover state={titleOpenAt} />
      <FleetingIdeaPanel
        setDraggingFleetingIdea={setDraggingFleetingIdea}
        colourMode={colourMode}
        setColourMode={setColourMode}
        locked={locked}
        setLocked={setLocked}
      />
      <div ref={ref} className="h-full w-full"></div>
    </>
  )
}

export default ShowPlateView
